// Agendador — o que dispara sozinho, quando, e o que parou de disparar.
//
// **O cron é gerado por formulário, não digitado.** É a decisão da PoC e ela
// está certa: expressão cron escrita à mão erra em silêncio, e o erro só
// aparece como uma camada que parou de atualizar sem ninguém perceber. Há um
// campo livre para quem precisa de algo que o formulário não cobre, e a API
// valida antes de gravar de qualquer jeito.
//
// O alerta de ATRASO é o que a PoC não tinha: falha tem linha vermelha, mas
// silêncio se parece com "está tudo bem".

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { alterar, obter, type ItemAgendador, type PainelAgendador } from "../api.js";
import { Carregando, Falha, quando, Vazio } from "../pecas/basicos.js";

const DIAS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

type Forma =
  | { tipo: "diaria"; hora: number; minuto: number }
  | { tipo: "semanal"; hora: number; minuto: number; diaSemana: number }
  | { tipo: "a_cada_horas"; passo: number }
  | { tipo: "personalizada" };

/** Lê a expressão de volta para o formulário. O que não couber vira "personalizada". */
export function interpretar(cron: string): Forma {
  const p = cron.trim().split(/\s+/);
  if (p.length !== 5) return { tipo: "personalizada" };
  const [minuto, hora, diaMes, mes, diaSemana] = p as [string, string, string, string, string];
  if (diaMes !== "*" || mes !== "*") return { tipo: "personalizada" };

  const passo = hora.match(/^\*\/(\d+)$/);
  if (passo && minuto === "0" && diaSemana === "*") {
    return { tipo: "a_cada_horas", passo: Number(passo[1]) };
  }
  const m = Number(minuto);
  const h = Number(hora);
  const numericos = /^\d+$/.test(minuto) && /^\d+$/.test(hora) && m <= 59 && h <= 23;
  if (!numericos) return { tipo: "personalizada" };
  if (diaSemana === "*") return { tipo: "diaria", hora: h, minuto: m };
  if (/^[0-6]$/.test(diaSemana)) {
    return { tipo: "semanal", hora: h, minuto: m, diaSemana: Number(diaSemana) };
  }
  return { tipo: "personalizada" };
}

export function gerar(f: Forma): string | null {
  if (f.tipo === "a_cada_horas") return `0 */${f.passo} * * *`;
  if (f.tipo === "diaria") return `${f.minuto} ${f.hora} * * *`;
  if (f.tipo === "semanal") return `${f.minuto} ${f.hora} * * ${f.diaSemana}`;
  return null;
}

export function emPortugues(cron: string): string {
  const f = interpretar(cron);
  const hm = (h: number, m: number): string =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  if (f.tipo === "diaria") return `Diariamente às ${hm(f.hora, f.minuto)}`;
  if (f.tipo === "semanal") return `Toda ${DIAS[f.diaSemana]}, às ${hm(f.hora, f.minuto)}`;
  if (f.tipo === "a_cada_horas") return `A cada ${f.passo} hora(s)`;
  return `Cadência personalizada (${cron})`;
}

export function Agendador({ avisar }: { avisar: (t: string, erro?: boolean) => void }): ReactNode {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<string | null>(null);

  const p = useQuery({
    queryKey: ["agendador"],
    queryFn: () => obter<PainelAgendador>("/agendador"),
    // O agendador muda sozinho: uma camada pode falhar enquanto a tela está
    // aberta, e quem está olhando precisa ver.
    refetchInterval: 60_000,
  });

  const salvar = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      alterar<unknown>(`/camadas/${id}/agendamento`, corpo),
    onSuccess: () => {
      avisar("Agendamento atualizado.");
      setEditando(null);
      void qc.invalidateQueries({ queryKey: ["agendador"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  if (p.error) return <Falha erro={p.error} />;
  // O DADO, não o status. Mesmo buraco do painel: `isLoading` falso com `data`
  // ainda indefinida derruba a árvore inteira em render.
  const dados = p.data;
  if (dados === undefined) return <Carregando />;

  return (
    <>
      <p className="discreto" style={{ marginTop: -12, marginBottom: 14 }}>
        As cadências são lidas no fuso <b className="mono">{dados.fuso}</b>. Os horários abaixo
        estão no seu relógio local.
      </p>

      {dados.alertas.length > 0 ? (
        <div className="pilha" style={{ marginBottom: 16 }}>
          {dados.alertas.map((a) => (
            <div
              key={a.camada_id}
              className={`aviso ${a.tipo === "atrasou" ? "" : "aviso-erro"}`}
              role="alert"
            >
              <b>
                {a.camada} —{" "}
                {a.tipo === "atrasou"
                  ? "sem executar"
                  : a.tipo === "falhou"
                    ? "última execução falhou"
                    : "cadência inválida"}
              </b>
              {a.detalhe}
            </div>
          ))}
        </div>
      ) : null}

      {dados.camadas.length === 0 ? (
        <Vazio>
          Nenhuma camada agendada. Uma camada é agendada quando está <b>ativa</b> e tem cadência —
          as duas coisas, não uma.
        </Vazio>
      ) : (
        <div className="rolagem">
          <table className="tabela">
            <thead>
              <tr>
                <th>Camada</th>
                <th>Cadência</th>
                <th>Próxima execução</th>
                <th>Publicação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dados.camadas.map((c) => (
                <LinhaAgendada
                  key={c.id}
                  item={c}
                  editando={editando === c.id}
                  aoEditar={() => setEditando(editando === c.id ? null : c.id)}
                  aoSalvar={(cron) => salvar.mutate({ id: c.id, corpo: { cadencia_sync: cron } })}
                  aoDesagendar={() => salvar.mutate({ id: c.id, corpo: { cadencia_sync: null } })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LinhaAgendada({
  item,
  editando,
  aoEditar,
  aoSalvar,
  aoDesagendar,
}: {
  item: ItemAgendador;
  editando: boolean;
  aoEditar: () => void;
  aoSalvar: (cron: string) => void;
  aoDesagendar: () => void;
}): ReactNode {
  return (
    <>
      <tr>
        <td>
          {item.nome}
          {item.alerta ? (
            <div className="discreto" style={{ color: "var(--err)" }}>
              {item.alerta.detalhe}
            </div>
          ) : null}
        </td>
        <td>
          <div>{emPortugues(item.cadencia_sync)}</div>
          <code className="mono discreto">{item.cadencia_sync}</code>
        </td>
        <td className="mono">
          {item.cadencia_invalida ? (
            <span style={{ color: "var(--err)" }}>{item.cadencia_invalida}</span>
          ) : (
            quando(item.proxima_execucao)
          )}
        </td>
        <td className="discreto">
          {item.modo_publicacao === "automatico" ? "valida e promove sozinho" : "espera curadoria"}
        </td>
        <td>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn" onClick={aoEditar}>
              {editando ? "Fechar" : "Editar"}
            </button>
            <button className="btn btn-perigo" onClick={aoDesagendar}>
              Desagendar
            </button>
          </div>
        </td>
      </tr>
      {editando ? (
        <tr>
          <td colSpan={5} style={{ background: "var(--sup2)" }}>
            <EditorCadencia cronAtual={item.cadencia_sync} aoSalvar={aoSalvar} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function EditorCadencia({
  cronAtual,
  aoSalvar,
}: {
  cronAtual: string;
  aoSalvar: (cron: string) => void;
}): ReactNode {
  const inicial = interpretar(cronAtual);
  const [tipo, setTipo] = useState<Forma["tipo"]>(inicial.tipo);
  const [hora, setHora] = useState("hora" in inicial ? inicial.hora : 4);
  const [minuto, setMinuto] = useState("minuto" in inicial ? inicial.minuto : 0);
  const [diaSemana, setDiaSemana] = useState("diaSemana" in inicial ? inicial.diaSemana : 1);
  const [passo, setPasso] = useState("passo" in inicial ? inicial.passo : 6);
  const [livre, setLivre] = useState(cronAtual);

  const montada =
    tipo === "personalizada"
      ? livre
      : (gerar(
          tipo === "a_cada_horas"
            ? { tipo, passo }
            : tipo === "semanal"
              ? { tipo, hora, minuto, diaSemana }
              : { tipo, hora, minuto },
        ) ?? livre);

  return (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "end", padding: "4px 0" }}
    >
      <div className="campo" style={{ marginBottom: 0 }}>
        <label htmlFor="tipo-cadencia">Repetir</label>
        <select
          id="tipo-cadencia"
          value={tipo}
          onChange={(e) => setTipo(e.target.value as Forma["tipo"])}
        >
          <option value="diaria">Todo dia</option>
          <option value="semanal">Toda semana</option>
          <option value="a_cada_horas">A cada N horas</option>
          <option value="personalizada">Expressão cron</option>
        </select>
      </div>

      {tipo === "semanal" ? (
        <div className="campo" style={{ marginBottom: 0 }}>
          <label htmlFor="dia-semana">Dia</label>
          <select
            id="dia-semana"
            value={diaSemana}
            onChange={(e) => setDiaSemana(Number(e.target.value))}
          >
            {DIAS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {tipo === "a_cada_horas" ? (
        <div className="campo" style={{ marginBottom: 0 }}>
          <label htmlFor="passo-horas">Horas</label>
          <input
            id="passo-horas"
            type="number"
            min={1}
            max={23}
            value={passo}
            onChange={(e) => setPasso(Number(e.target.value))}
            style={{ width: 80 }}
          />
        </div>
      ) : null}

      {tipo === "diaria" || tipo === "semanal" ? (
        <div className="campo" style={{ marginBottom: 0 }}>
          <label htmlFor="horario">Horário</label>
          <input
            id="horario"
            type="time"
            value={`${String(hora).padStart(2, "0")}:${String(minuto).padStart(2, "0")}`}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":");
              setHora(Number(h));
              setMinuto(Number(m));
            }}
            style={{ width: 130 }}
          />
        </div>
      ) : null}

      {tipo === "personalizada" ? (
        <div className="campo" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
          <label htmlFor="cron-livre">Expressão cron (5 campos)</label>
          <input id="cron-livre" value={livre} onChange={(e) => setLivre(e.target.value)} />
        </div>
      ) : null}

      <div style={{ flex: 1, minWidth: 180 }}>
        <div className="discreto">Vai ficar assim:</div>
        <b>{emPortugues(montada)}</b>
      </div>

      <button className="btn btn-primario" onClick={() => aoSalvar(montada)}>
        Salvar cadência
      </button>
    </div>
  );
}
